/**
 * Hall Booking Calendar - FullCalendar GemengApp
 * Calendrier spécifique pour la réservation de salles
 */

import { createCalendar, fetchBookedDaysForRange, findRentableId, formatDate, isOverlapping } from '../shared/_fullCalendar.js';
import { bookingHorizonEndExclusive } from '../shared/dateHelpers.js';

let fullCalendarGemengApp = null;

// Occupation mémorisée en scope module. FullCalendar reconstruit entièrement ses
// cellules à chaque prev()/next() : styler le DOM une seule fois après le fetch
// faisait disparaître les jours orange/rouges dès le premier changement de mois.
// Le cache est relu par dayCellClassNames/dayCellDidMount à CHAQUE rendu.
let bookedFullDays = [];
let bookedPartialSlotsByDate = {};

/**
 * Clé "YYYY-MM-DD" locale, alignée sur celle produite par buildBookedDays().
 */
function toDateKey(date) {
    return formatDate(date, 'english').date;
}

// Alignés sur les breakpoints Bootstrap utilisés dans _calendar.scss.
const BREAKPOINT_XL = 1200;
const BREAKPOINT_XXL = 1400;

/**
 * Vue à afficher pour une largeur d'écran donnée.
 */
function getViewForWidth(width) {
    if (width >= BREAKPOINT_XXL) return 'multiMonthThree';
    if (width >= BREAKPOINT_XL) return 'multiMonthTwo';
    return 'dayGridMonth';
}

const MOIS_PAR_VUE = { multiMonthThree: 3, multiMonthTwo: 2, dayGridMonth: 1 };

/**
 * Premier mois de la DERNIÈRE fenêtre entièrement contenue dans l'horizon.
 *
 * `validRange` seul ne suffit pas : FullCalendar laisse la fenêtre démarrer en
 * décembre 2029 et rend alors « Janvier 2030 » et « Février 2030 » en grilles
 * vides, l'horizon coupant leurs jours. On borne donc aussi le point de départ.
 */
function dernierDebutNavigable(nomVue) {
    const fin = bookingHorizonEndExclusive();          // 1er janvier de l'année +4
    const dernierMois = new Date(fin.getFullYear(), fin.getMonth() - 1, 1);  // décembre
    const mois = MOIS_PAR_VUE[nomVue] || 1;
    return new Date(dernierMois.getFullYear(), dernierMois.getMonth() - (mois - 1), 1);
}

/**
 * Sous xl, le CSS masque les flèches custom (positionnées pour les vues
 * multiMonth) : on rend alors la vraie toolbar FullCalendar, qui gère le
 * tactile et l'accessibilité. Sans elle, le calendrier n'était pas navigable
 * du tout sur mobile.
 */
function getHeaderToolbarForView(viewName) {
    return viewName === 'dayGridMonth'
        ? { left: 'prev', center: 'title', right: 'next' }
        : { left: '', center: '', right: '' };
}

/**
 * Calculate minimum booking date based on advance days
 * @param {number} advanceDays - Number of days in advance
 * @returns {Date} Minimum selectable date
 */
function getMinBookingDate(advanceDays) {
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + advanceDays);
    minDate.setHours(0, 0, 0, 0);
    return minDate;
}

/**
 * Libellés du calendrier, rendus par place-app.njk (le JS n'a pas accès à `_()`).
 * Les valeurs par défaut couvrent un gabarit plus ancien que le bundle.
 */
const LIBELLES_PAR_DEFAUT = {
    prev: 'Mois précédent',
    next: 'Mois suivant',
    free: 'Libre — aucune réservation ce jour',
    partial: 'Partiellement réservé : {slots}',
    full: 'Complet — plus aucun créneau libre',
    tooClose: "Trop tôt — il faut réserver au moins {n} jours à l'avance",
    pick: 'Sélectionnez un jour pour connaître ses disponibilités.',
};

let libelles = null;

function t(cle, params) {
    if (!libelles) {
        let fournis = {};
        const bloc = document.getElementById('calendarLabels');
        if (bloc) {
            try {
                fournis = JSON.parse(bloc.textContent) || {};
            } catch (e) {
                fournis = {};
            }
        }
        libelles = Object.assign({}, LIBELLES_PAR_DEFAUT, fournis);
    }
    let texte = libelles[cle] || LIBELLES_PAR_DEFAUT[cle] || '';
    Object.keys(params || {}).forEach(function (nom) {
        texte = texte.split('{' + nom + '}').join(params[nom]);
    });
    return texte;
}

/**
 * Affiche l'occupation d'un jour sous le calendrier.
 *
 * L'information ne vivait que dans l'attribut `title` des cellules, et une infobulle
 * native ne s'affiche JAMAIS au tactile : sur mobile, savoir qu'un samedi était pris
 * de 08:00 à 12:00 était donc impossible. Le tap la rend lisible, et `aria-live` la
 * fait annoncer aux lecteurs d'écran.
 */
function afficherDetailDuJour(date, minDayBooking) {
    const zone = document.querySelector('[data-calendar-day-detail]');
    if (!zone) return;

    const jour = new Date(date);
    jour.setHours(0, 0, 0, 0);
    const cleDate = toDateKey(jour);

    // « jeudi 1 octobre » n'est pas du français : le premier du mois prend l'ordinal.
    const dateLisible = jour
        .toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
        .replace(/\b1\b(?=\s)/, '1er');

    let etat;
    if (jour.getTime() < getMinBookingDate(minDayBooking).getTime()) {
        etat = t('tooClose', { n: minDayBooking });
    } else if (bookedFullDays.indexOf(cleDate) !== -1) {
        etat = t('full');
    } else if (bookedPartialSlotsByDate[cleDate]) {
        etat = t('partial', { slots: bookedPartialSlotsByDate[cleDate].join(', ') });
    } else {
        etat = t('free');
    }

    zone.textContent = dateLisible + ' — ' + etat;
    zone.classList.add('is-filled');
}

/**
 * Initialize FullCalendar for hall booking (read-only)
 */
export function initHallBookingCalendar() {
    const calendarElement = document.getElementById('fullCalendarGemengApp');
    if (!calendarElement) return;

    // Deux chemins mènent ici — `HallBookingModule.init()` et l'auto-initialisation
    // de fin de fichier — et ils ont vocation à coexister. Un second rendu poserait
    // un deuxième jeu de flèches de navigation et rejouerait tous les appels de
    // disponibilité.
    if (fullCalendarGemengApp) return;

    // Get minimum day booking value from data attribute
    const minDayBooking = parseInt(calendarElement.dataset.mindaybooking) || 5;

    const rentableId = findRentableId();
    if (!rentableId) {
        return;
    }

    const initialViewName = getViewForWidth(window.innerWidth);

    // Custom configuration for hall booking calendar
    const customConfig = {
        initialView: initialViewName,
        // Même fenêtre que les pickers du formulaire. Sans `validRange`, la navigation
        // était infinie dans les deux sens (2018 comme 2034 étaient atteignables, à
        // raison d'un appel API par saut) et donnait à consulter des mois que le
        // formulaire refuse ensuite de réserver.
        validRange: {
            start: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
            end: bookingHorizonEndExclusive(),
        },
        // Les vues multiMonth n'affichent l'année que si la fenêtre franchit un
        // 31 décembre : on lisait « juin | juillet | août » sans savoir de quelle
        // année, ce qui devient intenable sur un horizon de 3 ans.
        multiMonthTitleFormat: { month: 'long', year: 'numeric' },
        titleFormat: { month: 'long', year: 'numeric' },
        views: {
            multiMonthThree: {
                type: 'multiMonth',
                duration: { months: 3 },
                buttonText: '3 Mois'
            },
            multiMonthTwo: {
                type: 'multiMonth', 
                duration: { months: 2 },
                buttonText: '2 Mois'
            }
        },
        height: 'auto',
        selectable: false,
        selectMirror: false,
        dayMaxEvents: false,
        dayCellClassNames: function(arg) {
            const classes = [];
            const cellDate = new Date(arg.date);
            cellDate.setHours(0, 0, 0, 0);

            // Check if before minimum booking date
            const minBookingDate = getMinBookingDate(minDayBooking);
            if (cellDate.getTime() < minBookingDate.getTime()) {
                classes.push('fc-day-not-selectable');
                classes.push('fc-day-past');
            }

            // Occupation relue depuis le cache : survit à la navigation entre mois.
            const dateKey = toDateKey(arg.date);
            if (bookedFullDays.indexOf(dateKey) !== -1) {
                classes.push('fc-day-full-booked');
            } else if (bookedPartialSlotsByDate[dateKey]) {
                classes.push('fc-day-partial-booked');
            }

            return classes;
        },
        dayCellDidMount: function(arg) {
            const slots = bookedPartialSlotsByDate[toDateKey(arg.date)];
            if (slots) {
                arg.el.title = `Partiellement réservé: ${slots.join(', ')}`;
            }
        },
        // Les intitulés des commandes viennent du gabarit : sans eux, FullCalendar
        // compose ses siens en anglais (« Previous Mois »), y compris pour les
        // lecteurs d'écran.
        buttonHints: {
            prev: t('prev'),
            next: t('next'),
        },
        dateClick: function (info) {
            afficherDetailDuJour(info.date, minDayBooking);
        },
        headerToolbar: getHeaderToolbarForView(initialViewName),
        datesSet: function(info) {
            // Recentrage si la navigation a dépassé la dernière fenêtre pleine.
            const debutVue = info.view.currentStart;
            const debutMois = new Date(debutVue.getFullYear(), debutVue.getMonth(), 1);
            const maxDebut = dernierDebutNavigable(info.view.type);
            if (debutMois.getTime() > maxDebut.getTime()) {
                fullCalendarGemengApp.gotoDate(maxDebut);
                return;
            }

            updateCalendarNavigation(info);
            // Ne charger que ce qui est à l'écran. `datesSet` se déclenche aussi au
            // premier rendu : c'est donc l'unique point d'entrée du chargement, et
            // chaque prev/next ne coûte que la portion pas encore connue (souvent rien).
            loadRangeForView(rentableId, info.start, info.end);
        }
    };

    fullCalendarGemengApp = createCalendar('fullCalendarGemengApp', customConfig);

    if (fullCalendarGemengApp) {
        fullCalendarGemengApp.render();

        const zoneDetail = document.querySelector('[data-calendar-day-detail]');
        if (zoneDetail) zoneDetail.textContent = t('pick');

        if (initialViewName !== 'dayGridMonth') {
            setupCustomNavigation();
        }
        setupResponsiveView();
    }
}

/**
 * Bascule vue + navigation quand on franchit un breakpoint.
 * La vue n'était calculée qu'au chargement : un simple pivot de tablette
 * laissait une vue 2/3 mois sans aucun contrôle de navigation.
 */
function setupResponsiveView() {
    let currentViewName = getViewForWidth(window.innerWidth);
    let resizeTimer = null;

    window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
            const nextViewName = getViewForWidth(window.innerWidth);
            if (!fullCalendarGemengApp || nextViewName === currentViewName) return;

            currentViewName = nextViewName;
            fullCalendarGemengApp.setOption('headerToolbar', getHeaderToolbarForView(nextViewName));
            fullCalendarGemengApp.changeView(nextViewName);

            if (nextViewName === 'dayGridMonth') {
                removeCustomNavigation();
            } else {
                setupCustomNavigation();
            }
        }, 200);
    });
}

/**
 * Charge l'occupation de la plage affichée, puis repeint.
 *
 * Le cache d'`apiClient` étant cumulatif et la repeinture idempotente, l'ordre
 * d'arrivée des réponses lors d'une navigation rapide est sans conséquence.
 */
function loadRangeForView(rentableId, start, end) {
    if (!rentableId) return;
    fetchBookedDaysForRange(rentableId, start, end, (bookedData) => {
        updateCalendarWithBookedDays(bookedData);
    });
}

/**
 * Mémorise l'occupation puis rafraîchit l'affichage.
 *
 * Le cache alimente dayCellClassNames/dayCellDidMount, donc tous les rendus
 * SUIVANTS (navigation entre mois, changement de vue) sont colorés d'office.
 * La passe impérative ne sert qu'aux cellules déjà montées quand le fetch arrive.
 */
function updateCalendarWithBookedDays(bookedData) {
    const { full, partial } = bookedData;

    bookedFullDays = full || [];

    // Un jour peut avoir PLUSIEURS créneaux occupés (cœur de la fonctionnalité
    // « 2 résas/jour ») -> on les regroupe par date pour que l'info-bulle les
    // liste tous au lieu de n'afficher que le dernier.
    bookedPartialSlotsByDate = (partial || []).reduce((acc, booking) => {
        (acc[booking.date] = acc[booking.date] || []).push(
            `${booking.startTime} - ${booking.endTime}`
        );
        return acc;
    }, {});

    applyBookedStylingToRenderedCells();
}

/**
 * Applique le cache aux cellules actuellement dans le DOM.
 */
function applyBookedStylingToRenderedCells() {
    const calendarElement = document.getElementById('fullCalendarGemengApp');
    if (!calendarElement) return;

    calendarElement.querySelectorAll('.fc-daygrid-day[data-date]').forEach(dayEl => {
        const dateKey = dayEl.dataset.date;

        dayEl.classList.remove('fc-day-full-booked', 'fc-day-partial-booked');
        dayEl.removeAttribute('title');

        if (bookedFullDays.indexOf(dateKey) !== -1) {
            dayEl.classList.add('fc-day-full-booked');
        } else if (bookedPartialSlotsByDate[dateKey]) {
            dayEl.classList.add('fc-day-partial-booked');
            dayEl.title = `Partiellement réservé: ${bookedPartialSlotsByDate[dateKey].join(', ')}`;
        }
    });
}

/**
 * Setup custom navigation buttons
 */
function setupCustomNavigation() {
    // Remove default navigation and add custom buttons
    const calendarElement = document.getElementById('fullCalendarGemengApp');
    if (!calendarElement) return;

    // Idempotent : setupResponsiveView peut rappeler cette fonction.
    if (calendarElement.querySelector('.custom-nav-buttons')) return;

    // Create custom navigation buttons
    const prevBtn = document.createElement('button');
    prevBtn.className = 'custom-nav-buttons fc-prev-button';
    prevBtn.innerHTML = '<i class="fa-regular fa-chevron-left"></i>';
    prevBtn.addEventListener('click', () => {
        if (fullCalendarGemengApp) {
            fullCalendarGemengApp.prev();
        }
    });
    
    const nextBtn = document.createElement('button');
    nextBtn.className = 'custom-nav-buttons fc-next-button';
    nextBtn.innerHTML = '<i class="fa-regular fa-chevron-right"></i>';
    nextBtn.addEventListener('click', () => {
        if (fullCalendarGemengApp) {
            fullCalendarGemengApp.next();
        }
    });
    
    calendarElement.appendChild(prevBtn);
    calendarElement.appendChild(nextBtn);
}

/**
 * Retire les flèches custom (bascule vers la toolbar native en dessous de xl).
 */
function removeCustomNavigation() {
    document
        .querySelectorAll('#fullCalendarGemengApp .custom-nav-buttons')
        .forEach(btn => btn.remove());
}

/**
 * Update navigation button positions based on calendar layout
 */
function updateCalendarNavigation(info) {
    const prevBtn = document.querySelector('.custom-nav-buttons.fc-prev-button');
    const nextBtn = document.querySelector('.custom-nav-buttons.fc-next-button');
    
    if (!prevBtn || !nextBtn) return;
    
    const currentView = info.view.type;
    
    // Adjust button positions based on view
    if (currentView === 'multiMonthThree') {
        prevBtn.style.left = '0px';
        nextBtn.style.right = '0px';
    } else if (currentView === 'multiMonthTwo') {
        prevBtn.style.left = '15px';
        nextBtn.style.right = '15px';
    }
}

/**
 * Refresh calendar data
 */
export function refreshHallBookingCalendar() {
    if (!fullCalendarGemengApp) return;

    const rentableId = findRentableId();
    if (!rentableId) return;

    // Recharger la plage effectivement affichée, pas un horizon arbitraire.
    const view = fullCalendarGemengApp.view;
    loadRangeForView(rentableId, view.activeStart, view.activeEnd);
}

/**
 * Get calendar instance
 */
export function getHallBookingCalendar() {
    return fullCalendarGemengApp;
}

// Auto-initialize on DOM load
document.addEventListener('DOMContentLoaded', function() {
    initHallBookingCalendar();
});
